// next-app/src/types/index.ts

// ============================================================================
// Enums
// ============================================================================

export enum UserRole {
  basic = 0,
  manager = 1,
  admin = 2,
  dealer = 3,
  hotel = 4,
  partner = 5,
  partner_crm = 6,
  planner = 7,
}

export enum DealerType {
  L = 'L',
  FL = 'FL',
}

export enum DealerRegion {
  CE = 'CE',
  EMA = 'EMA',
  GL = 'GL',
  SE = 'SE',
  W = 'W',
}

export enum EFGEventStatus {
  new = 'new',
  eligible = 'eligible',
  planning = 'planning',
  review = 'review',
  approved = 'approved',
  'in progress' = 'in progress',
  'event ending' = 'event ending',
  'event ended' = 'event ended',
  'donation approved' = 'donation approved',
  ineligible = 'ineligible',
  revise = 'revise',
  cancelled = 'canceled',
  reimbursement_requested = 'reimbursement requested',
  reimbursement_approved = 'reimbursement approved',
}

export enum EFGEventType {
  rejuvenate_only = 'rejuvenate_only',
  test_drive_rejuvenate = 'test_drive_rejuvenate',
}

export type EventType = 'rejuvenate_only' | 'test_drive_rejuvenate';

export type EnrollmentStatus =
  | 'pre_enrollment'
  | 'enrollment_open'
  | 'waitlist_open'
  | 'enrollment_closed';

// ============================================================================
// Domain Types
// ============================================================================

export type Dealer = {
  dealer_id: number;
  dealer_name: string;
  dealer_type?: DealerType;
  parts_code: string;
  address: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  dealer_region?: DealerRegion;
  primary_contact: string;
  contact_email: string;
  contact_phone: string;
  active: boolean;
  attended_training: boolean;
  training_attendee_name: string;
  training_date: Date;
  waitlist?: boolean;
};

export type Charity = {
  charity_id: number;
  name: string;
  address_1: string;
  address_2?: string;
  city: string;
  state: string;
  postal_code: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_phone_number: string;
  contact_email: string;
  description: string;
  website: string;
  tax_id: string;
};

export type EFGSeason = {
  season_id: number;
  season_name: string;
  start_date: Date;
  end_date: Date;
  enrollment_status?: EnrollmentStatus;
};

export type EFGEvent = {
  event_id?: number;
  event_request_id?: number;
  event_name?: string;
  event_series_id?: number;
  event_type?: EventType;
  bonus_event?: boolean;
  season_id: number;
  dealer_id: number;
  requested_month?: string;
  requested_date?: Date;
  sort_date?: Date;
  display_date?: string;
  start_date?: Date;
  end_date?: Date;
  start_time?: Date;
  end_time?: Date;
  time_zone?: string;
  ffs_event_id?: number;
  drive_event_id?: number;
  training_completed?: Date;
  training_completed_by?: string;
  vincent_code?: string;
  vincent_value?: number;
  pre_reg_hash?: string;
  cancelled?: boolean;
  ended?: boolean;
  check_data_processed?: Date;
  dealer_reminder_email_sent?: Date;
  charity_partner_id?: number;
  charity_partner?: string;
  event_location?: string;
  event_location_name?: string;
  address?: string;
  city?: string;
  zip_code?: string;
  state?: string;
  digital_event_code?: string;
  logged_in?: Date;
  spreadsheet_data?: string;
  invalid_address?: boolean;
  edit_log?: string;
  sort_status?: number;
  status?: EFGEventStatus;
  created_by_user_id?: number;
  created_timestamp: Date;
  updated_timestamp: Date;
  location_contact?: string;
  location_phone?: string;
  location_email?: string;
  funding_purpose?: string;
  attendance?: number;
  rejected?: boolean;
  check_location?: string;
  check_payable?: string;
  check_attention?: string;
  check_address?: string;
  check_address_2?: string;
  check_city?: string;
  check_state?: string;
  check_zip_code?: string;
  eligibility_custom_data?: string;
  event_custom_data?: string;
  reimbursement_requested?: Date;
};

export type UserDetails = {
  user_id: number;
  user_role: UserRole;
  email: string;
  display_name: string;
  phone_number: string;
  active: boolean;
  dtg_user: boolean;
  activation_partner_id: number[];
};

export type Respondent = {
  first_name: string;
  last_name: string;
  survey_date: string;
};

// ============================================================================
// API Types
// ============================================================================

export type ApiResponse<T = unknown> = {
  response: Response;
  result?: T;
};

export type ApiError = {
  errors?: string[];
  message?: string;
};

// Login response from POST v1/auth/signin
export type LoginResponse = {
  token: string;
  dealer: Dealer;
};

// Account response from GET v1/auth/account
export type AccountResponse = {
  dealer: Dealer;
};

// Events list response from GET v1/event/list
export type EventsListResponse = {
  events: EFGEvent[];
};

// Single event response from GET v1/event/request/{id}
export type EventResponse = {
  event: EFGEvent;
  charity?: Charity;
  planner?: UserDetails;
  errors?: string[];
};

// Eligibility check response from GET v1/event/check-request
export type EligibilityCheckResponse = {
  eligible: boolean;
  reason?: string;
};

// Respondents list response from GET v1/event/list-respondents/{id}
export type RespondentsResponse = {
  respondents: Respondent[];
};

// 2FA response from POST v1/event/send-2fa-email/{email}
export type TwoFAResponse = {
  code: string;
};

// Event token response from POST v1/auth/create_login_token
export type EventTokenResponse = {
  token: string;
  url: string;
};

// ============================================================================
// UI Types
// ============================================================================

export type ListItemType = {
  Name: string;
  Value: string;
  Id: string | number;
};

// ============================================================================
// Custom Data Parsed Types
// ============================================================================

/**
 * Parsed structure of eligibility_custom_data JSON field.
 */
export type EligibilityCustomData = {
  requested_month?: string;
  requested_date?: Date;
  deny_message?: string;
};

/**
 * Parsed structure of event_custom_data JSON field.
 */
export type EventCustomData = {
  centuryParty?: EFGEventType;
  event_type?: EventType;
  file_id?: string;
  file_name?: string;
};

// ============================================================================
// Survey Types
// ============================================================================

export type SurveyQuestionType =
  | 'text'
  | 'radio'
  | 'checkbox'
  | 'select'
  | 'date'
  | 'phone'
  | 'email';

export type SurveyQuestion = {
  id: string;
  type: SurveyQuestionType;
  label: string;
  required: boolean;
  options?: string[];
};

export type SurveyType = 'pre' | 'post' | 'basic' | 'rsvp' | 'check-in';

export type SurveyResponse = {
  survey_response_id?: number;
  event_id: number;
  hash: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  survey_type: SurveyType;
  responses: Record<string, unknown>;
  waiver_accepted?: boolean;
  email_opt_in?: boolean;
  created_timestamp?: Date;
};

export type SurveyConfig = {
  event_id: number;
  event_name: string;
  event_type: EventType;
  dealer_name?: string;
  vehicles?: Vehicle[];
  waiver_text?: string;
  email_opt_in_text?: string;
};

// ============================================================================
// Participant Types (Check-in/out)
// ============================================================================

export type Participant = {
  participant_id: number;
  event_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  checked_in?: Date;
  checked_in_by?: string;
  checked_out?: Date;
  checked_out_by?: string;
  test_drove?: boolean;
  vehicles_interested: number[];
  vehicles_driven: number[];
  notes?: string;
};

export type CheckInRecord = {
  participant_id: number;
  checked_in: Date;
  checked_in_by: string;
};

export type CheckOutRecord = {
  participant_id: number;
  checked_out: Date;
  checked_out_by: string;
  test_drove: boolean;
  vehicles_driven: number[];
};

// ============================================================================
// Vehicle Types
// ============================================================================

export type Vehicle = {
  vehicle_id: number;
  name: string;
  model_year: number;
  image_url?: string;
  is_black_label?: boolean;
  sort_order: number;
};

export type VehicleOfInterest = {
  participant_id: number;
  vehicle_id: number;
  interest_level?: 'high' | 'medium' | 'low';
};

// ============================================================================
// Training Types
// ============================================================================

export type TrainingProgress = {
  dealer_id: number;
  event_id?: number;
  video_watched: boolean;
  overview_completed: boolean;
  details_completed: boolean;
  assets_downloaded: boolean;
  completed_date?: Date;
  completed_by?: string;
};

export type TrainingAsset = {
  asset_id: number;
  name: string;
  description: string;
  file_url: string;
  file_type: string;
};

// ============================================================================
// Survey API Response Types
// ============================================================================

export type SurveyConfigResponse = {
  config: SurveyConfig;
};

export type EventSurveyConfig = {
  event_id: number;
  event_name: string;
  event_type: EventType;
};

export type SurveySubmitResponse = {
  success: boolean;
  participant_id?: number;
  message?: string;
};

// ============================================================================
// Check-in/out API Response Types
// ============================================================================

export type CheckInDataResponse = {
  participants: Participant[];
  vehicles: Vehicle[];
  event: EFGEvent;
};

export type CheckOutDataResponse = {
  participants: Participant[];
  vehicles: Vehicle[];
  event: EFGEvent;
};

// ============================================================================
// Stats API Response Types
// ============================================================================

export type EventStatsResponse = {
  total_registered: number;
  checked_in: number;
  checked_out: number;
  test_drives: number;
  vehicles_driven: Record<number, number>;
  hourly_check_ins: { hour: string; count: number }[];
};

// ============================================================================
// Training API Response Types
// ============================================================================

export type TrainingProgressResponse = {
  progress: TrainingProgress;
};

// ============================================================================
// Utility API Response Types
// ============================================================================

export type HashToTokenResponse = {
  token: string;
  event_id: number;
};

export type MagicLoginResponse = {
  token: string;
  dealer: Dealer;
  event_id: number;
};

// Event Portal Login Response from POST /auth/signin with eventId
export type EventPortalLoginResponse = {
  token: string;
  event: EFGEvent;
};

// ============================================================================
// Legacy Type Aliases (for compatibility)
// ============================================================================

/**
 * Standard return type for API calls.
 * Alias for ApiResponse for compatibility with phase document.
 */
export type DTGFetchResult<T = unknown> = ApiResponse<T>;

/**
 * Error response structure from API.
 * Alias for ApiError for compatibility with phase document.
 */
export type ErrorResult = ApiError;
